import * as am4core from '@amcharts/amcharts4/core';
import { ForceDirectedLink, ForceDirectedSeries } from '@amcharts/amcharts4/plugins/forceDirected';
import * as i18next from 'i18next';

import { CONSTANTS } from '../../../../../../core/config/constants';
import { Classifier, GraphLink, GraphNode } from '../../../../../../generated/model/models';
import { ConnectionsFromInterface } from '../ConnectionsContext/ConnectionsContext.types';
import {
  ConnectionsWeightEnum,
  GraphEntityTypeEnum,
  GraphHistoricalTypeEnum,
  GraphLinkTextEnum,
  GraphLinkTypeEnum,
  GraphStrengthTypeEnum,
  Icons,
  IconsType,
  LINK_STROKE_DASH,
  ManagementTypeEnum,
  MappedGraphNode,
  NodesColorEnum,
  NodesRadiustEnum,
  VisibilityTypes,
} from './ConnectionsChart.type';

const FORMMANAGEMENTTYPES = [
  ManagementTypeEnum.BENEFICIAL_OWNER,
  ManagementTypeEnum.SHAREHOLDER,
  ManagementTypeEnum.ADMINISTRATOR,
  ManagementTypeEnum.AUDITOR,
  ManagementTypeEnum.MEMBER_OF_THE_BOARD,
  ManagementTypeEnum.BRANCH_MANAGER,
  ManagementTypeEnum.EMPOWERED_TO_SIGN,
  ManagementTypeEnum.FOUNDER,
  ManagementTypeEnum.LIABLE_PARTNER,
  ManagementTypeEnum.GENERAL_AGENT,
  ManagementTypeEnum.LIQUIDATOR,
  ManagementTypeEnum.MANAGEMENT,
  ManagementTypeEnum.OTHER,
  ManagementTypeEnum.MEMBER_OF_SUPERVISORY_BOARD,
];

export function nodesToggler(
  data: MappedGraphNode[],
  formValues: ConnectionsFromInterface,
  mappedLinks: any,
  setFilteredMappedLinks: any,
  setFormManagementTypes: any,
) {
  const companyType = formValues.companies.value ? GraphEntityTypeEnum.COMPANY : null;
  const branchType = formValues.branches.value ? GraphEntityTypeEnum.BRANCH : null;
  const personType =
    formValues.persons.value || formValues.persons.indeterminate
      ? GraphEntityTypeEnum.PERSON
      : null;
  const phoneType = formValues.phone.value ? GraphEntityTypeEnum.PHONE : null;
  const emailType = formValues.email.value ? GraphEntityTypeEnum.EMAIL : null;
  const addressType = formValues.address.value ? GraphEntityTypeEnum.ADDRESS : null;
  const websiteType = formValues.website.value ? GraphEntityTypeEnum.WEBSITE : null;
  const beneficialOwner = formValues.beneficialOwner.value
    ? ManagementTypeEnum.BENEFICIAL_OWNER
    : null;
  const shareholder = formValues.shareholder.value ? ManagementTypeEnum.SHAREHOLDER : null;
  const admininstrator = formValues.administrator.value ? ManagementTypeEnum.ADMINISTRATOR : null;
  const auditor = formValues.auditor.value ? ManagementTypeEnum.AUDITOR : null;
  const memberOfTheBoard = formValues.memberOfTheBoard.value
    ? ManagementTypeEnum.MEMBER_OF_THE_BOARD
    : null;
  const branchManager = formValues.branchManager.value ? ManagementTypeEnum.BRANCH_MANAGER : null;
  const empoweredToSign = formValues.empoweredToSign.value
    ? ManagementTypeEnum.EMPOWERED_TO_SIGN
    : null;
  const founder = formValues.founder.value ? ManagementTypeEnum.FOUNDER : null;
  const liablePartner = formValues.liablePartner.value ? ManagementTypeEnum.LIABLE_PARTNER : null;
  const generalAgent = formValues.generalAgent.value ? ManagementTypeEnum.GENERAL_AGENT : null;
  const liquidator = formValues.liquidator.value ? ManagementTypeEnum.LIQUIDATOR : null;
  const management = formValues.management.value ? ManagementTypeEnum.MANAGEMENT : null;
  const other = formValues.other.value ? ManagementTypeEnum.OTHER : null;
  const memberOfSupervisoryBoard = formValues.memberOfSupervisoryBoard.value
    ? ManagementTypeEnum.MEMBER_OF_SUPERVISORY_BOARD
    : null;
  const formManagementTypes = [
    beneficialOwner,
    shareholder,
    admininstrator,
    auditor,
    memberOfTheBoard,
    branchManager,
    empoweredToSign,
    founder,
    liablePartner,
    generalAgent,
    liquidator,
    management,
    other,
    memberOfSupervisoryBoard,
  ];
  setFormManagementTypes(formManagementTypes);

  let mainCompanyId = '';
  data.forEach((item: MappedGraphNode) => {
    if (item.entityType === GraphEntityTypeEnum.COMPANY && !formValues.companies.indeterminate) {
      item.hidden = false;
    }
    if (item.entityType === GraphEntityTypeEnum.BRANCH && !formValues.branches.indeterminate) {
      item.hidden = false;
    }
    if (item.entityType === GraphEntityTypeEnum.PERSON) {
      item.hidden = false;
    }
    if (item.entityType === GraphEntityTypeEnum.EMAIL && !formValues.email.indeterminate) {
      item.hidden = false;
    }
    if (item.entityType === GraphEntityTypeEnum.PHONE && !formValues.phone.indeterminate) {
      item.hidden = false;
    }
    if (item.entityType === GraphEntityTypeEnum.ADDRESS && !formValues.address.indeterminate) {
      item.hidden = false;
    }
    if (item.entityType === GraphEntityTypeEnum.WEBSITE && !formValues.website.indeterminate) {
      item.hidden = false;
    }

    if (item.value === NodesRadiustEnum.MAIN_COMPANY && item.id) {
      mainCompanyId = item.id;
    }
  });

  const filteredRows = data.filter((item: MappedGraphNode) => {
    return (
      (item.entityType === companyType ||
        item.entityType === branchType ||
        item.entityType === personType ||
        item.entityType === phoneType ||
        item.entityType === emailType ||
        item.entityType === websiteType ||
        item.entityType === addressType ||
        item.id === mainCompanyId) &&
      item.hidden !== true
    );
  });

  const filteredNodeIds = filteredRows.map((item: any) => item.id);

  const removeHistoricalLink = Object.values(mappedLinks).filter((item: any) => {
    if (
      filteredNodeIds.indexOf(item.nodeId) >= 0 &&
      filteredNodeIds.indexOf(item.connectedNodeId) >= 0
    ) {
      return formValues.historicalType.value
        ? item.historicalType === GraphHistoricalTypeEnum.CURRENT
        : true;
    }
    return false;
  });

  const filteredByFormLinks: any = {};

  removeHistoricalLink.forEach((link: any) => {
    let keyLink = '';
    if (link.managementType) {
      keyLink =
        link.nodeId +
        '_' +
        link.connectedNodeId +
        '_' +
        link.historicalType +
        '_' +
        link.managementType;
    } else if (link.ownershipType) {
      keyLink =
        link.nodeId +
        '_' +
        link.connectedNodeId +
        '_' +
        link.historicalType +
        '_' +
        link.ownershipType;
    } else {
      keyLink = link.nodeId + '_' + link.connectedNodeId + '_' + link.historicalType;
    }

    if (link.relationType === `${GraphEntityTypeEnum.COMPANY}_${GraphEntityTypeEnum.COMPANY}`) {
      filteredByFormLinks[keyLink] = link;
    }

    if (link.managementType) {
      if (formManagementTypes.indexOf(link.managementType) !== -1) {
        filteredByFormLinks[keyLink] = link;
      }
    } else if (link.ownershipType) {
      if (formManagementTypes.indexOf(link.ownershipType) !== -1) {
        filteredByFormLinks[keyLink] = link;
      }
    } else {
      filteredByFormLinks[keyLink] = link;
    }
  });

  setFilteredMappedLinks(filteredByFormLinks);

  // get all connected unique nodes from links
  const nodeIds: any[] = getUniqueNodeIds(filteredByFormLinks, mainCompanyId);

  if (nodeIds.indexOf(mainCompanyId) === -1) {
    nodeIds.push(mainCompanyId);
  }

  const updatedData = filteredRows.filter((item: MappedGraphNode) => {
    return nodeIds.indexOf(item.id) !== -1;
  });

  const filteredUnlinkedNodes = removeUnlinkedNodes(updatedData, mainCompanyId);
  return filteredUnlinkedNodes;
}

function removeUnlinkedNodes(filteredRows: MappedGraphNode[], mainCompanyId: string) {
  const filteredNodeIds: any[] = [];
  let filteredLinkIds: any[] = [];

  filteredRows.forEach((item: MappedGraphNode) => {
    if (item.id) {
      filteredNodeIds.push(item.id);
    }
    if (item.linkWith) {
      filteredLinkIds = filteredLinkIds.concat(item.linkWith);
    }
  });

  const filteredRowsExcludedUnlinked = filteredRows.filter((item: MappedGraphNode) => {
    const hasValidLink = item.linkWith?.filter((nodeId) => {
      return filteredNodeIds.indexOf(nodeId) >= 0;
    }).length;

    return filteredLinkIds.indexOf(item.id) >= 0 || hasValidLink || item.id === mainCompanyId;
  });
  return filteredRowsExcludedUnlinked;
}

export function generateNodes(
  data: any[],
  mappedLinks: any,
  companyUid: string,
  countryCodeToNameMap: Map<string, string>,
) {
  const mappedConnectedNodes: any = {};
  if (data) {
    data.forEach((element) => {
      if (element.linkWith.length) {
        element.textKeywords = [];
        element.allHistoricalTypes = [];

        element.linkWith.forEach((link: any) => {
          const ownerEntityType = data.find((d) => d.id === link.nodeId)?.entityType;
          const connectedEntityType = data.find((dataTo) => dataTo.id === link.connectedNodeId)
            ?.entityType;
          link.textKeywords = [];
          link.relationType = `${ownerEntityType}_${connectedEntityType}`;
          if (link) {
            let keyLink = '';
            if (link.managementType) {
              keyLink =
                link.nodeId +
                '_' +
                link.connectedNodeId +
                '_' +
                link.historicalType +
                '_' +
                link.managementType;
            } else if (link.ownershipType) {
              keyLink =
                link.nodeId +
                '_' +
                link.connectedNodeId +
                '_' +
                link.historicalType +
                '_' +
                link.ownershipType;
            } else {
              keyLink = link.nodeId + '_' + link.connectedNodeId + '_' + link.historicalType;
            }

            if (typeof mappedLinks[keyLink] !== 'undefined') {
              mappedLinks[keyLink].linkStrength += ',' + link.linkStrength;
              mappedLinks[keyLink].entityType = element.entityType;
              if (mappedLinks[keyLink].textKeywords.indexOf(setTextKeyword(link)) === -1) {
                mappedLinks[keyLink].textKeywords.push(setTextKeyword(link));
              }
            } else {
              // save all links
              mappedLinks[keyLink] = link;
              if (mappedLinks[keyLink].textKeywords.indexOf(setTextKeyword(link)) === -1) {
                mappedLinks[keyLink].textKeywords.push(setTextKeyword(link));
              }
            }

            // mapped all nodes
            if (typeof mappedConnectedNodes[link.connectedNodeId] !== 'undefined') {
              mappedConnectedNodes[link.connectedNodeId] += ',' + link.nodeId;
            } else {
              mappedConnectedNodes[link.connectedNodeId] = link.nodeId;
            }

            element.allHistoricalTypes.push(link.historicalType);
          }
        });
      }

      if (!element.name) {
        element.name = CONSTANTS.UNKNOWN_NAME;
      }
      // set linkWith like array
      const linkWithIds = element.linkWith.map((item: any) => item.connectedNodeId);

      // set unique id for linkWith
      element.linkWith = linkWithIds.filter((item: string, pos: number) => {
        return linkWithIds.indexOf(item) === pos;
      });

      // set Icons
      const key: keyof IconsType = element.entityType;
      element.path = Icons[key];

      // set hidden nodes onload
      if (mappedConnectedNodes[element.id]) {
        const nodeArray = mappedConnectedNodes[element.id].split(',');

        if (nodeArray.length > CONSTANTS.CONNECTION_NODES_HIDDEN_COUNT) {
          element.hidden = true;
        } else {
          element.hidden = false;
        }
      }

      element.value = setNodesRadius(element, companyUid);
      element.color = setNodesBackground(element, companyUid);
      element.countryLabel =
        element.country && element.entityType === GraphEntityTypeEnum.COMPANY
          ? countryCodeToNameMap.get(element.country)
          : '';
    });
  }
  // setNodeLinkChild(data);

  return mappedLinks;
}

export const setNodesBackground = (element: MappedGraphNode, companyUid: string) => {
  if (element.companyUid && element.companyUid === companyUid) {
    return NodesColorEnum.MAIN_COMPANY;
  }

  switch (element.entityType) {
    case GraphEntityTypeEnum.COMPANY:
      return NodesColorEnum.COMPANY;
    case GraphEntityTypeEnum.BRANCH:
      return NodesColorEnum.BRANCH;
    case GraphEntityTypeEnum.PERSON:
      return NodesColorEnum.PERSON;
    case GraphEntityTypeEnum.PHONE:
      return NodesColorEnum.PHONE;
    case GraphEntityTypeEnum.EMAIL:
      return NodesColorEnum.EMAIL;
    case GraphEntityTypeEnum.WEBSITE:
      return NodesColorEnum.WEBSITE;
    case GraphEntityTypeEnum.ADDRESS:
      return NodesColorEnum.ADDRESS;
  }
};

export const setTooltipLinks = (
  classifierRealtionType: Classifier[],
  classifierOwnershipType: Classifier[],
  series: ForceDirectedSeries,
  t: i18next.TFunction,
  hiddenClass: string,
  mappedLinks: any,
  formManagementTypes: any,
) => {
  series.links.template.adapter.add('tooltipHTML', (_: any, target: ForceDirectedLink) => {
    const from: any = target.source.dataItem.dataContext;
    const to: any = target.target.dataItem.dataContext;
    let text: any = '';
    let textDirect: any = [];
    let textInverse: any = [];

    Object.values(mappedLinks).forEach((item: any) => {
      if (item.nodeId === from.id && item.connectedNodeId === to.id) {
        textDirect = [...textDirect, ...item.textKeywords];
      }
    });
    Object.values(mappedLinks).forEach((item: any) => {
      if (item.nodeId === to.id && item.connectedNodeId === from.id) {
        textInverse = [...textInverse, ...item.textKeywords];
      }
    });

    const generatedDirectTexts = textDirect
      .map((text: string) => {
        return generateText(
          text,
          from,
          to,
          t,
          classifierRealtionType,
          classifierOwnershipType,
          hiddenClass,
          formManagementTypes,
        );
      })
      .join('\n');

    const generatedInverseTexts = textInverse
      .map((text: string) => {
        return generateText(
          text,
          to,
          from,
          t,
          classifierRealtionType,
          classifierOwnershipType,
          hiddenClass,
          formManagementTypes,
        );
      })
      .join('\n');

    text = generatedDirectTexts.toString() + generatedInverseTexts.toString();

    return text;
  });
};

export const historicalText = (
  relationType: string,
  historicalText: string,
  currentText: string,
) => {
  return relationType === GraphHistoricalTypeEnum.HISTORICAL ? historicalText : currentText;
};

export const generateText = (
  textType: string,
  from: any,
  to: any,
  t: i18next.TFunction,
  classifierReletionType: Classifier[],
  classifierOwnershipType: Classifier[],
  hiddenClass: string,
  formManagementTypes: string[],
) => {
  const isCompaniesRelation = from.entityType === GraphEntityTypeEnum.COMPANY;

  let classifierText = '';
  let genText = '';
  const managementTypes = isCompaniesRelation ? FORMMANAGEMENTTYPES : formManagementTypes;
  if (textType.indexOf(GraphLinkTypeEnum.MANAGEMENT) !== -1) {
    ({ classifierText, genText } = setClassifierText(
      textType,
      classifierReletionType,
      GraphLinkTypeEnum.MANAGEMENT,
      managementTypes,
    ));
  }

  if (textType.indexOf(GraphLinkTypeEnum.OWNERSHIP) !== -1) {
    ({ classifierText, genText } = setClassifierText(
      textType,
      classifierOwnershipType,
      GraphLinkTypeEnum.OWNERSHIP,
      managementTypes,
    ));
  }

  if (textType.indexOf(GraphLinkTypeEnum.HOLDING) !== -1) {
    ({ classifierText, genText } = setClassifierText(
      textType,
      classifierOwnershipType,
      GraphLinkTypeEnum.HOLDING,
    ));
  }

  const text = genText ? genText : textType;
  switch (text) {
    case GraphLinkTextEnum.CURRENT_OWNERSHIP:
      return `<p>${from.name} ${t('connections:is')} ${t('connections:a')} ${classifierText} ${t(
        'connections:of',
      )} ${to.name}</p>`;
    case GraphLinkTextEnum.HISTORICAL_OWNERSHIP:
      return `<p class="${hiddenClass}">${from.name} ${t('connections:was')} ${t(
        'connections:a',
      )} ${classifierText} ${t('connections:of')} ${to.name}</p>`;
    case GraphLinkTextEnum.CURRENT_HOLDING:
      return `<p>${to.name} ${t('connections:is')} ${t('connections:a')} ${classifierText} ${t(
        'connections:of',
      )} ${from.name}</p>`;
    case GraphLinkTextEnum.HISTORICAL_HOLDING:
      return `<p class="${hiddenClass}">${to.name} ${t('connections:was')} ${t(
        'connections:a',
      )} ${classifierText} ${t('connections:of')} ${from.name}</p>`;
    case GraphLinkTextEnum.CURRENT_BRANCH:
      return `<p>${to.name} ${t('connections:is')} ${t('connections:branchOf')} ${from.name}</p>`;
    case GraphLinkTextEnum.HISTORICAL_BRANCH:
      return `<p class="${hiddenClass}">${to.name} ${t('connections:was')} ${t(
        'connections:branchOf',
      )} ${from.name}</p>`;
    case GraphLinkTextEnum.CURRENT_MANAGEMENT:
      if (from.entityType === GraphEntityTypeEnum.COMPANY) {
        return `<p>${to.name} ${t('connections:is')} ${classifierText} ${t('connections:at')} ${
          from.name
        }</p>`;
      } else {
        return `<p>${from.name} ${t('connections:is')} ${classifierText} ${t('connections:at')} ${
          to.name
        }</p>`;
      }
    case GraphLinkTextEnum.HISTORICAL_MANAGEMENT:
      if (from.entityType === GraphEntityTypeEnum.COMPANY) {
        return `<p class="${hiddenClass}">${to.name} ${t('connections:was')} ${classifierText} ${t(
          'connections:at',
        )} ${from.name}</p>`;
      } else {
        return `<p class="${hiddenClass}">${from.name} ${t(
          'connections:was',
        )} ${classifierText} ${t('connections:at')} ${to.name}<p>`;
      }
    case GraphLinkTextEnum.CURRENT_PHONE:
      return `<p>${from.name} ${t('connections:has')} ${t('connections:registeredPhone')}</p>`;
    case GraphLinkTextEnum.HISTORICAL_PHONE:
      return `<p class="${hiddenClass}">${from.name} ${t('connections:had')} ${t(
        'connections:registeredPhone',
      )}</p>`;
    case GraphLinkTextEnum.CURRENT_EMAIL:
      return `<p>${from.name} ${t('connections:has')} ${t('connections:registeredEmail')}</p>`;
    case GraphLinkTextEnum.HISTORICAL_EMAIL:
      return `<p class="${hiddenClass}">${from.name} ${t('connections:had')} ${t(
        'connections:registeredEmail',
      )}</p>`;
    case GraphLinkTextEnum.CURRENT_WEBSITE:
      return `<p>${from.name} ${t('connections:has')} ${t('connections:registeredWebsite')}</p>`;
    case GraphLinkTextEnum.HISTORICAL_WEBSITE:
      return `<p class="${hiddenClass}">${from.name} ${t('connections:had')} ${t(
        'connections:registeredWebsite',
      )}</p>`;
    case GraphLinkTextEnum.CURRENT_ADDRESS:
      return `<p>${from.name} ${t('connections:has')} ${t('connections:registeredAddress')}</p>`;
    case GraphLinkTextEnum.HISTORICAL_ADDRESS:
      return `<p class="${hiddenClass}">${from.name} ${t('connections:had')} ${t(
        'connections:registeredAddress',
      )}</p>`;
    case GraphLinkTextEnum.CURRENT_WEBSITE_ADDRESS:
      return `<p>${t('connections:currentAddressWebsiteMatch')}</p>`;
    case GraphLinkTextEnum.HISTORICAL_WEBSITE_ADDRESS:
      return `<p class="${hiddenClass}">${t('connections:historicalAddressWebsiteMatch')}</p>`;
    default:
      return null;
  }
};

export const setStrokeWidth = (series: any, mappedLinks: any) => {
  series.links.template.adapter.add('strokeWidth', (width: number, target: ForceDirectedLink) => {
    const from: any = target.source.dataItem.dataContext;
    const to: any = target.target.dataItem.dataContext;
    // generate linkStrength for links
    Object.values(mappedLinks).forEach((item: any) => {
      if (item.nodeId === from.id && item.connectedNodeId === to.id) {
        return (from.linkStrength = item?.linkStrength);
      }
      return width;
    });
    Object.values(mappedLinks).forEach((item: any) => {
      if (item.nodeId === to.id && item.connectedNodeId === from.id) {
        return (from.linkStrength = item?.linkStrength);
      }
      return width;
    });

    if (from.linkStrength) {
      if (from.linkStrength.indexOf(GraphStrengthTypeEnum.STRONG) >= 0) {
        return ConnectionsWeightEnum.Strong;
      }
      if (from.linkStrength.indexOf(GraphStrengthTypeEnum.WEAK) >= 0) {
        return ConnectionsWeightEnum.Weak;
      }
    }

    if (to.linkStrength) {
      if (to.linkStrength.indexOf(GraphStrengthTypeEnum.STRONG) >= 0) {
        return ConnectionsWeightEnum.Strong;
      }
      if (to.linkStrength.indexOf(GraphStrengthTypeEnum.WEAK) >= 0) {
        return ConnectionsWeightEnum.Weak;
      }
    }

    return width;
  });
};

export const setHistoricalStroke = (series: any, mappedLinks: any) => {
  series.links.template.adapter.add(
    'strokeDasharray',
    (width: number, target: ForceDirectedLink) => {
      const from: GraphNode = target.source.dataItem.dataContext;
      const to: GraphNode = target.target.dataItem.dataContext;

      // filtered by nodeId and connectedNodeId from mappedLinks
      const filteredLinksById = Object.values(mappedLinks).filter((item: any) => {
        return (
          (item.nodeId === from.id && item.connectedNodeId === to.id) ||
          (item.nodeId === to.id && item.connectedNodeId === from.id)
        );
      });

      if (filteredLinksById.length) {
        // if one of link has current set as current
        const isCurrent = filteredLinksById.some(
          (item: any) => item.historicalType === GraphHistoricalTypeEnum.CURRENT,
        );
        if (isCurrent) {
          return width;
        } else {
          return LINK_STROKE_DASH;
        }
      }
      return ['0,1'];
    },
  );
};

export const setNodesRadius = (element: MappedGraphNode, companyUid: string) => {
  if (element.companyUid && element.companyUid === companyUid) {
    return NodesRadiustEnum.MAIN_COMPANY;
  }

  switch (element.entityType) {
    case GraphEntityTypeEnum.COMPANY:
      return NodesRadiustEnum.COMPANY;
    case GraphEntityTypeEnum.BRANCH:
      return NodesRadiustEnum.BRANCH;
    case GraphEntityTypeEnum.PERSON:
      return NodesRadiustEnum.PERSON;
    case GraphEntityTypeEnum.PHONE:
      return NodesRadiustEnum.PHONE;
    case GraphEntityTypeEnum.EMAIL:
      return NodesRadiustEnum.EMAIL;
    case GraphEntityTypeEnum.WEBSITE:
      return NodesRadiustEnum.WEBSITE;
    case GraphEntityTypeEnum.ADDRESS:
      return NodesRadiustEnum.ADDRESS;
  }
  return element;
};

export const setNodeLinkChild = (data: MappedGraphNode[]) => {
  data.forEach((item: MappedGraphNode) => {
    item.childCount = data.filter((el: any) => el.linkWith.includes(item.id)).length;
  });

  return data;
};

export const setTextKeyword = (link: GraphLink) => {
  const keyText = link.historicalType + '_' + link.linkType;
  let linkKeywords = '';
  if (link.linkType === GraphLinkTypeEnum.MANAGEMENT) {
    linkKeywords = keyText + '_' + link.managementType;
  } else if (
    link.linkType === GraphLinkTypeEnum.OWNERSHIP ||
    link.linkType === GraphLinkTypeEnum.HOLDING
  ) {
    linkKeywords = keyText + '_' + link.ownershipType;
  } else {
    linkKeywords = keyText;
  }

  return linkKeywords;
};

function setClassifierText(
  textType: string,
  classifierReletionType: Classifier[],
  graphLinkKeyword: string,
  formManagementTypes?: string[],
) {
  let classifierText = '';
  let genText = '';

  const splitText = textType.split(graphLinkKeyword + '_');
  const classifierType = classifierReletionType.find((item: Classifier) => {
    return item.id === splitText[1];
  });
  if (formManagementTypes) {
    if (formManagementTypes.indexOf(splitText[1]) >= 0) {
      classifierText = classifierType?.name ?? CONSTANTS.UNKNOWN_NAME;
      genText = splitText[0] + graphLinkKeyword;
    }
  } else {
    classifierText = classifierType?.name ?? CONSTANTS.UNKNOWN_NAME;
    genText = splitText[0] + graphLinkKeyword;
  }

  return { classifierText, genText };
}

export function setCompanyToCenter(data: MappedGraphNode[], companyUid: string) {
  data.map((element: MappedGraphNode) => {
    if (element.companyUid && element.companyUid === companyUid) {
      element.x = am4core.percent(50);
      element.y = am4core.percent(50);
      element.fixed = true;
    }
    return element;
  });

  return data;
}

export function getUniqueNodeIds(filteredLinks: any, mainCompanyId: string) {
  const nodeIds: string[] = [];

  let nodeIdsToProcess: string[] = [mainCompanyId];
  while (nodeIdsToProcess.length) {
    nodeIdsToProcess = findConnectedNodes(filteredLinks, nodeIdsToProcess, nodeIds);
  }

  return nodeIds;
}

function findConnectedNodes(links: any, nodeIdsToProcess: string[], nodeIds: string[]) {
  const newNodes: string[] = [];
  Object.values(links).forEach((link: any) => {
    nodeIdsToProcess.forEach((id) => {
      if (link.nodeId === id) {
        if (nodeIds.indexOf(link.connectedNodeId) === -1) {
          newNodes.push(link.connectedNodeId);
          nodeIds.push(link.connectedNodeId);
        }
      }
      if (link.connectedNodeId === id) {
        if (nodeIds.indexOf(link.nodeId) === -1) {
          newNodes.push(link.nodeId);
          nodeIds.push(link.nodeId);
        }
      }
    });
  });
  return newNodes;
}

export function setMappedNodes(data: any) {
  const mappedConnectedNodes: any = {};
  data.forEach((element: any) => {
    element.linkWith.forEach((link: any) => {
      if (link) {
        if (typeof mappedConnectedNodes[link.connectedNodeId] !== 'undefined') {
          mappedConnectedNodes[link.connectedNodeId] += ',' + link.nodeId;
        } else {
          mappedConnectedNodes[link.connectedNodeId] = link.nodeId;
        }
        if (typeof mappedConnectedNodes[link.nodeId] !== 'undefined') {
          mappedConnectedNodes[link.nodeId] += ',' + link.connectedNodeId;
        } else {
          mappedConnectedNodes[link.nodeId] = link.connectedNodeId;
        }
      }
    });
  });
  return mappedConnectedNodes;
}

export function setHiddenNodes(
  data: MappedGraphNode[],
  mappedConnectedNodes: any,
  mappedLinks: any,
) {
  const nodesTypes: VisibilityTypes = {
    hidden: [],
    visible: [],
  };
  const linkTypes: VisibilityTypes = {
    hidden: [],
    visible: [],
  };
  const nodeHiddenTypes: string[] = [];
  const nodeVisibleTypes: string[] = [];
  const linkHiddenTypes: string[] = [];
  const linkVisibleTypes: string[] = [];

  data.forEach((item: any) => {
    if (mappedConnectedNodes[item.id]) {
      const nodeArray = mappedConnectedNodes[item.id].split(',');

      if (nodeArray.length > CONSTANTS.CONNECTION_NODES_HIDDEN_COUNT) {
        //set element hidden
        item.hidden = true;
        // set link hiddenTypes
        if (item.linkWith.length) {
          item.linkWith.forEach((link: any) => {
            if (link.linkType === GraphLinkTypeEnum.OWNERSHIP) {
              if (linkHiddenTypes.indexOf(link.ownershipType) === -1) {
                linkHiddenTypes.push(link.ownershipType);
              }
            }
            if (link.linkType === GraphLinkTypeEnum.MANAGEMENT) {
              if (linkHiddenTypes.indexOf(link.managementType) === -1) {
                linkHiddenTypes.push(link.managementType);
              }
            }
          });
        } else {
          const found: any = Object.values(mappedLinks).find(
            (e: any) => e.connectedNodeId === item.id,
          );
          if (found.linkType === GraphLinkTypeEnum.OWNERSHIP) {
            if (linkHiddenTypes.indexOf(found.ownershipType) === -1) {
              linkHiddenTypes.push(found.ownershipType);
            }
          }
          if (found.linkType === GraphLinkTypeEnum.MANAGEMENT) {
            if (linkHiddenTypes.indexOf(found.managementType) === -1) {
              linkHiddenTypes.push(found.managementType);
            }
          }
        }
        if (nodeHiddenTypes.indexOf(item.entityType) === -1) {
          nodeHiddenTypes.push(item.entityType);
        }
      } else {
        //set element hidden
        item.hidden = false;
        // set link hiddenTypes
        if (item.linkWith.length) {
          item.linkWith.forEach((link: any) => {
            // set linkVisibleTypes
            if (link.linkType === GraphLinkTypeEnum.OWNERSHIP) {
              if (linkVisibleTypes.indexOf(link.ownershipType) === -1) {
                linkVisibleTypes.push(link.ownershipType);
              }
            }
            if (link.linkType === GraphLinkTypeEnum.MANAGEMENT) {
              if (linkVisibleTypes.indexOf(link.managementType) === -1) {
                linkVisibleTypes.push(link.managementType);
              }
            }
          });
        }
        if (nodeVisibleTypes.indexOf(item.entityType) === -1) {
          nodeVisibleTypes.push(item.entityType);
        }
      }
    }
  });

  // remove from visibleLinks if hiddenLinks exist
  const filteredLinks = linkVisibleTypes.filter((el) => {
    return linkHiddenTypes.indexOf(el) < 0;
  });

  nodesTypes.hidden = nodeHiddenTypes;
  nodesTypes.visible = nodeVisibleTypes;

  linkTypes.hidden = linkHiddenTypes;
  linkTypes.visible = filteredLinks;

  return [nodesTypes, linkTypes];
}

export function setExportingData(series: ForceDirectedSeries) {
  series.exporting.adapter.add('data', (data) => {
    data.data = [];
    data.data = series.data;
    return data;
  });
}

export function generateFormValues(
  nodeVisibilityTypes: VisibilityTypes,
  linkVisibilityType: VisibilityTypes,
  form: ConnectionsFromInterface,
): ConnectionsFromInterface {
  return {
    companies: {
      value:
        nodeVisibilityTypes.hidden.indexOf(GraphEntityTypeEnum.COMPANY) === -1 ||
        (nodeVisibilityTypes.hidden.indexOf(GraphEntityTypeEnum.COMPANY) !== -1 &&
          nodeVisibilityTypes.visible.indexOf(GraphEntityTypeEnum.COMPANY) !== -1)
          ? true
          : false,
      indeterminate:
        nodeVisibilityTypes.hidden.indexOf(GraphEntityTypeEnum.COMPANY) !== -1 &&
        nodeVisibilityTypes.visible.indexOf(GraphEntityTypeEnum.COMPANY) !== -1
          ? true
          : false,
    },
    branches: {
      value:
        nodeVisibilityTypes.hidden.indexOf(GraphEntityTypeEnum.BRANCH) === -1 ||
        (nodeVisibilityTypes.hidden.indexOf(GraphEntityTypeEnum.BRANCH) !== -1 &&
          nodeVisibilityTypes.visible.indexOf(GraphEntityTypeEnum.BRANCH) !== -1)
          ? true
          : false,
      indeterminate:
        nodeVisibilityTypes.hidden.indexOf(GraphEntityTypeEnum.BRANCH) !== -1 &&
        nodeVisibilityTypes.visible.indexOf(GraphEntityTypeEnum.BRANCH) !== -1
          ? true
          : false,
    },
    persons: {
      value:
        nodeVisibilityTypes.hidden.indexOf(GraphEntityTypeEnum.PERSON) === -1 ||
        (nodeVisibilityTypes.hidden.indexOf(GraphEntityTypeEnum.PERSON) !== -1 &&
          nodeVisibilityTypes.visible.indexOf(GraphEntityTypeEnum.PERSON) !== -1)
          ? true
          : false,
      indeterminate:
        nodeVisibilityTypes.hidden.indexOf(GraphEntityTypeEnum.PERSON) !== -1 &&
        nodeVisibilityTypes.visible.indexOf(GraphEntityTypeEnum.PERSON) !== -1
          ? true
          : false,
    },
    phone: {
      value:
        nodeVisibilityTypes.hidden.indexOf(GraphEntityTypeEnum.PHONE) === -1 ||
        (nodeVisibilityTypes.hidden.indexOf(GraphEntityTypeEnum.PHONE) !== -1 &&
          nodeVisibilityTypes.visible.indexOf(GraphEntityTypeEnum.PHONE) !== -1)
          ? true
          : false,
      indeterminate:
        nodeVisibilityTypes.hidden.indexOf(GraphEntityTypeEnum.PHONE) !== -1 &&
        nodeVisibilityTypes.visible.indexOf(GraphEntityTypeEnum.PHONE) !== -1
          ? true
          : false,
    },
    email: {
      value:
        nodeVisibilityTypes.hidden.indexOf(GraphEntityTypeEnum.EMAIL) === -1 ||
        (nodeVisibilityTypes.hidden.indexOf(GraphEntityTypeEnum.EMAIL) !== -1 &&
          nodeVisibilityTypes.visible.indexOf(GraphEntityTypeEnum.EMAIL) !== -1)
          ? true
          : false,
      indeterminate:
        nodeVisibilityTypes.hidden.indexOf(GraphEntityTypeEnum.EMAIL) !== -1 &&
        nodeVisibilityTypes.visible.indexOf(GraphEntityTypeEnum.EMAIL) !== -1
          ? true
          : false,
    },
    address: {
      value:
        nodeVisibilityTypes.hidden.indexOf(GraphEntityTypeEnum.ADDRESS) === -1 ||
        (nodeVisibilityTypes.hidden.indexOf(GraphEntityTypeEnum.ADDRESS) !== -1 &&
          nodeVisibilityTypes.visible.indexOf(GraphEntityTypeEnum.ADDRESS) !== -1)
          ? true
          : false,
      indeterminate:
        nodeVisibilityTypes.hidden.indexOf(GraphEntityTypeEnum.ADDRESS) !== -1 &&
        nodeVisibilityTypes.visible.indexOf(GraphEntityTypeEnum.ADDRESS) !== -1
          ? true
          : false,
    },
    website: {
      value:
        nodeVisibilityTypes.hidden.indexOf(GraphEntityTypeEnum.WEBSITE) === -1 ||
        (nodeVisibilityTypes.hidden.indexOf(GraphEntityTypeEnum.WEBSITE) !== -1 &&
          nodeVisibilityTypes.visible.indexOf(GraphEntityTypeEnum.WEBSITE) !== -1)
          ? true
          : false,
      indeterminate:
        nodeVisibilityTypes.hidden.indexOf(GraphEntityTypeEnum.WEBSITE) !== -1 &&
        nodeVisibilityTypes.visible.indexOf(GraphEntityTypeEnum.WEBSITE) !== -1
          ? true
          : false,
    },
    shareholder: {
      value:
        linkVisibilityType.hidden.indexOf(ManagementTypeEnum.SHAREHOLDER) === -1 ||
        (linkVisibilityType.hidden.indexOf(ManagementTypeEnum.SHAREHOLDER) !== -1 &&
          linkVisibilityType.visible.indexOf(ManagementTypeEnum.SHAREHOLDER) !== -1)
          ? true
          : false,
      indeterminate:
        linkVisibilityType.hidden.indexOf(ManagementTypeEnum.SHAREHOLDER) !== -1 &&
        linkVisibilityType.visible.indexOf(ManagementTypeEnum.SHAREHOLDER) !== -1
          ? true
          : false,
    },
    beneficialOwner: {
      value:
        linkVisibilityType.hidden.indexOf(ManagementTypeEnum.BENEFICIAL_OWNER) === -1 ||
        (linkVisibilityType.hidden.indexOf(ManagementTypeEnum.BENEFICIAL_OWNER) !== -1 &&
          linkVisibilityType.visible.indexOf(ManagementTypeEnum.BENEFICIAL_OWNER) !== -1)
          ? true
          : false,
      indeterminate:
        linkVisibilityType.hidden.indexOf(ManagementTypeEnum.BENEFICIAL_OWNER) !== -1 &&
        linkVisibilityType.visible.indexOf(ManagementTypeEnum.BENEFICIAL_OWNER) !== -1
          ? true
          : false,
    },
    administrator: {
      value:
        linkVisibilityType.hidden.indexOf(ManagementTypeEnum.ADMINISTRATOR) === -1 ||
        (linkVisibilityType.hidden.indexOf(ManagementTypeEnum.ADMINISTRATOR) !== -1 &&
          linkVisibilityType.visible.indexOf(ManagementTypeEnum.ADMINISTRATOR) !== -1)
          ? true
          : false,
      indeterminate:
        linkVisibilityType.hidden.indexOf(ManagementTypeEnum.ADMINISTRATOR) !== -1 &&
        linkVisibilityType.visible.indexOf(ManagementTypeEnum.ADMINISTRATOR) !== -1
          ? true
          : false,
    },
    auditor: {
      value:
        linkVisibilityType.hidden.indexOf(ManagementTypeEnum.AUDITOR) === -1 ||
        (linkVisibilityType.hidden.indexOf(ManagementTypeEnum.AUDITOR) !== -1 &&
          linkVisibilityType.visible.indexOf(ManagementTypeEnum.AUDITOR) !== -1)
          ? true
          : false,
      indeterminate:
        linkVisibilityType.hidden.indexOf(ManagementTypeEnum.AUDITOR) !== -1 &&
        linkVisibilityType.visible.indexOf(ManagementTypeEnum.AUDITOR) !== -1
          ? true
          : false,
    },
    memberOfTheBoard: {
      value:
        linkVisibilityType.hidden.indexOf(ManagementTypeEnum.MEMBER_OF_THE_BOARD) === -1 ||
        (linkVisibilityType.hidden.indexOf(ManagementTypeEnum.MEMBER_OF_THE_BOARD) !== -1 &&
          linkVisibilityType.visible.indexOf(ManagementTypeEnum.MEMBER_OF_THE_BOARD) !== -1)
          ? true
          : false,
      indeterminate:
        linkVisibilityType.hidden.indexOf(ManagementTypeEnum.MEMBER_OF_THE_BOARD) !== -1 &&
        linkVisibilityType.visible.indexOf(ManagementTypeEnum.MEMBER_OF_THE_BOARD) !== -1
          ? true
          : false,
    },
    branchManager: {
      value:
        linkVisibilityType.hidden.indexOf(ManagementTypeEnum.BRANCH_MANAGER) === -1 ||
        (linkVisibilityType.hidden.indexOf(ManagementTypeEnum.BRANCH_MANAGER) !== -1 &&
          linkVisibilityType.visible.indexOf(ManagementTypeEnum.BRANCH_MANAGER) !== -1)
          ? true
          : false,
      indeterminate:
        linkVisibilityType.hidden.indexOf(ManagementTypeEnum.BRANCH_MANAGER) !== -1 &&
        linkVisibilityType.visible.indexOf(ManagementTypeEnum.BRANCH_MANAGER) !== -1
          ? true
          : false,
    },
    empoweredToSign: {
      value:
        linkVisibilityType.hidden.indexOf(ManagementTypeEnum.EMPOWERED_TO_SIGN) === -1 ||
        (linkVisibilityType.hidden.indexOf(ManagementTypeEnum.EMPOWERED_TO_SIGN) !== -1 &&
          linkVisibilityType.visible.indexOf(ManagementTypeEnum.EMPOWERED_TO_SIGN) !== -1)
          ? true
          : false,
      indeterminate:
        linkVisibilityType.hidden.indexOf(ManagementTypeEnum.EMPOWERED_TO_SIGN) !== -1 &&
        linkVisibilityType.visible.indexOf(ManagementTypeEnum.EMPOWERED_TO_SIGN) !== -1
          ? true
          : false,
    },
    founder: {
      value:
        linkVisibilityType.hidden.indexOf(ManagementTypeEnum.FOUNDER) === -1 ||
        (linkVisibilityType.hidden.indexOf(ManagementTypeEnum.FOUNDER) !== -1 &&
          linkVisibilityType.visible.indexOf(ManagementTypeEnum.FOUNDER) !== -1)
          ? true
          : false,
      indeterminate:
        linkVisibilityType.hidden.indexOf(ManagementTypeEnum.FOUNDER) !== -1 &&
        linkVisibilityType.visible.indexOf(ManagementTypeEnum.FOUNDER) !== -1
          ? true
          : false,
    },
    liablePartner: {
      value:
        linkVisibilityType.hidden.indexOf(ManagementTypeEnum.LIABLE_PARTNER) === -1 ||
        (linkVisibilityType.hidden.indexOf(ManagementTypeEnum.LIABLE_PARTNER) !== -1 &&
          linkVisibilityType.visible.indexOf(ManagementTypeEnum.LIABLE_PARTNER) !== -1)
          ? true
          : false,
      indeterminate:
        linkVisibilityType.hidden.indexOf(ManagementTypeEnum.LIABLE_PARTNER) !== -1 &&
        linkVisibilityType.visible.indexOf(ManagementTypeEnum.LIABLE_PARTNER) !== -1
          ? true
          : false,
    },
    generalAgent: {
      value:
        linkVisibilityType.hidden.indexOf(ManagementTypeEnum.GENERAL_AGENT) === -1 ||
        (linkVisibilityType.hidden.indexOf(ManagementTypeEnum.GENERAL_AGENT) !== -1 &&
          linkVisibilityType.visible.indexOf(ManagementTypeEnum.GENERAL_AGENT) !== -1)
          ? true
          : false,
      indeterminate:
        linkVisibilityType.hidden.indexOf(ManagementTypeEnum.GENERAL_AGENT) !== -1 &&
        linkVisibilityType.visible.indexOf(ManagementTypeEnum.GENERAL_AGENT) !== -1
          ? true
          : false,
    },
    liquidator: {
      value:
        linkVisibilityType.hidden.indexOf(ManagementTypeEnum.LIQUIDATOR) === -1 ||
        (linkVisibilityType.hidden.indexOf(ManagementTypeEnum.LIQUIDATOR) !== -1 &&
          linkVisibilityType.visible.indexOf(ManagementTypeEnum.LIQUIDATOR) !== -1)
          ? true
          : false,
      indeterminate:
        linkVisibilityType.hidden.indexOf(ManagementTypeEnum.LIQUIDATOR) !== -1 &&
        linkVisibilityType.visible.indexOf(ManagementTypeEnum.LIQUIDATOR) !== -1
          ? true
          : false,
    },
    management: {
      value:
        linkVisibilityType.hidden.indexOf(ManagementTypeEnum.MANAGEMENT) === -1 ||
        (linkVisibilityType.hidden.indexOf(ManagementTypeEnum.MANAGEMENT) !== -1 &&
          linkVisibilityType.visible.indexOf(ManagementTypeEnum.MANAGEMENT) !== -1)
          ? true
          : false,
      indeterminate:
        linkVisibilityType.hidden.indexOf(ManagementTypeEnum.MANAGEMENT) !== -1 &&
        linkVisibilityType.visible.indexOf(ManagementTypeEnum.MANAGEMENT) !== -1
          ? true
          : false,
    },
    other: {
      value:
        linkVisibilityType.hidden.indexOf(ManagementTypeEnum.OTHER) === -1 ||
        (linkVisibilityType.hidden.indexOf(ManagementTypeEnum.OTHER) !== -1 &&
          linkVisibilityType.visible.indexOf(ManagementTypeEnum.OTHER) !== -1)
          ? true
          : false,
      indeterminate:
        linkVisibilityType.hidden.indexOf(ManagementTypeEnum.OTHER) !== -1 &&
        linkVisibilityType.visible.indexOf(ManagementTypeEnum.OTHER) !== -1
          ? true
          : false,
    },
    memberOfSupervisoryBoard: {
      value:
        linkVisibilityType.hidden.indexOf(ManagementTypeEnum.MEMBER_OF_SUPERVISORY_BOARD) === -1 ||
        (linkVisibilityType.hidden.indexOf(ManagementTypeEnum.MEMBER_OF_SUPERVISORY_BOARD) !== -1 &&
          linkVisibilityType.visible.indexOf(ManagementTypeEnum.MEMBER_OF_SUPERVISORY_BOARD) !== -1)
          ? true
          : false,
      indeterminate:
        linkVisibilityType.hidden.indexOf(ManagementTypeEnum.MEMBER_OF_SUPERVISORY_BOARD) !== -1 &&
        linkVisibilityType.visible.indexOf(ManagementTypeEnum.MEMBER_OF_SUPERVISORY_BOARD) !== -1
          ? true
          : false,
    },
    historicalType: {
      value: form.historicalType.value,
      indeterminate: form.historicalType.indeterminate,
    },
  };
}
